/**
 * Step indicator component
 * Visual progress indicator for multi-step forms
 */

import { Steps, Typography } from 'antd';
import { CheckOutlined } from '@ant-design/icons';

const { Text } = Typography;

interface Step {
  key: string;
  title: string;
  description?: string;
}

interface StepIndicatorProps {
  steps: Step[];
  currentStep: number;
  onStepClick?: (step: number) => void;
}

export function StepIndicator({ steps, currentStep, onStepClick }: StepIndicatorProps) {
  return (
    <Steps
      current={currentStep}
      onChange={onStepClick}
      items={steps.map((step, index) => ({
        key: step.key,
        title: step.title,
        description: step.description,
        status: index < currentStep ? 'finish' : index === currentStep ? 'process' : 'wait',
      }))}
      style={{ maxWidth: 800, margin: '0 auto' }}
    />
  );
}

/**
 * Compact step indicator (numbered circles)
 */
export function StepIndicatorCompact({
  steps,
  currentStep,
  onStepClick,
}: StepIndicatorProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
      }}
    >
      {steps.map((step, index) => {
        const isCompleted = index < currentStep;
        const isCurrent = index === currentStep;
        const isClickable = onStepClick && index < currentStep;

        return (
          <div
            key={step.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <div
              onClick={isClickable ? () => onStepClick(index) : undefined}
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
                fontWeight: 500,
                cursor: isClickable ? 'pointer' : 'default',
                transition: 'all 0.2s',
                ...(isCompleted
                  ? {
                      background: 'var(--color-primary)',
                      color: '#fff',
                    }
                  : isCurrent
                  ? {
                      background: 'var(--color-primary)',
                      color: '#fff',
                      boxShadow: '0 0 0 4px var(--color-primary-bg)',
                    }
                  : {
                      background: 'var(--color-bg-spotlight)',
                      color: 'var(--color-text-tertiary)',
                      border: '1px solid var(--color-border)',
                    }),
              }}
            >
              {isCompleted ? <CheckOutlined /> : index + 1}
            </div>

            {/* Connector line */}
            {index < steps.length - 1 && (
              <div
                style={{
                  width: 40,
                  height: 2,
                  background: isCompleted
                    ? 'var(--color-primary)'
                    : 'var(--color-border)',
                  transition: 'background 0.2s',
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Step indicator with labels below
 */
export function StepIndicatorWithLabels({
  steps,
  currentStep,
  onStepClick,
}: StepIndicatorProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
      }}
    >
      {steps.map((step, index) => {
        const isCompleted = index < currentStep;
        const isCurrent = index === currentStep;
        const isClickable = onStepClick && index < currentStep;

        return (
          <div
            key={step.key}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              position: 'relative',
              minWidth: 100,
            }}
          >
            {/* Circle with number */}
            <div
              onClick={isClickable ? () => onStepClick(index) : undefined}
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 16,
                fontWeight: 500,
                cursor: isClickable ? 'pointer' : 'default',
                zIndex: 1,
                ...(isCompleted
                  ? {
                      background: 'var(--color-primary)',
                      color: '#fff',
                    }
                  : isCurrent
                  ? {
                      background: 'var(--color-primary)',
                      color: '#fff',
                      boxShadow: '0 0 0 4px var(--color-primary-bg)',
                    }
                  : {
                      background: '#fff',
                      color: 'var(--color-text-tertiary)',
                      border: '2px solid var(--color-border)',
                    }),
              }}
            >
              {isCompleted ? <CheckOutlined /> : index + 1}
            </div>

            {/* Label */}
            <Text
              style={{
                marginTop: 8,
                fontSize: 13,
                fontWeight: isCurrent ? 500 : 400,
                color: isCurrent ? 'var(--color-text)' : 'var(--color-text-secondary)',
                textAlign: 'center',
              }}
            >
              {step.title}
            </Text>

            {/* Connector line */}
            {index < steps.length - 1 && (
              <div
                style={{
                  position: 'absolute',
                  top: 18,
                  left: '50%',
                  width: '100%',
                  height: 2,
                  background: isCompleted
                    ? 'var(--color-primary)'
                    : 'var(--color-border)',
                  zIndex: 0,
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default StepIndicator;
